import { KeyboardArrowLeft } from "@mui/icons-material";
import { Button, CircularProgress } from "@mui/material";
import { StripeError } from "@stripe/stripe-js";
import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import AddPaymentMethod, {
  AddPaymentMethodRef,
} from "components/Payment/UpgradeFlowComponents/AddPaymentMethod";
import { Subscription } from "models/User";
import { setDefaultPaymentMethod } from "services/SubscriptionService";

import styles from "../ProfilePage.module.scss";

interface UpgradePaymentPanelProps {
  subscription: Subscription;
  onBack: () => void;
  onPaymentMethodReady: () => void;
}

const UpgradePaymentPanel: React.FC<UpgradePaymentPanelProps> = ({
  subscription,
  onBack,
  onPaymentMethodReady,
}) => {
  const addPaymentMethodRef = useRef<AddPaymentMethodRef>(null);
  const location = useLocation();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className={styles.billingTabRoot}>
      <div className={styles.developerUsageSection}>
        <div className={styles.developerBack} onClick={onBack}>
          <KeyboardArrowLeft fontSize="small" />
          <span>Back</span>
        </div>

        <h5>Add a payment method</h5>
        <p className={styles.developerEmptyText}>
          Autopilot API usage is billed weekly. Add a card to upgrade to the
          paid tier.
        </p>

        {error && <p className={styles.developerError}>{error}</p>}

        <AddPaymentMethod
          ref={addPaymentMethodRef}
          onSuccess={async (setupIntent) => {
            setError(null);
            setSaving(true);
            try {
              await setDefaultPaymentMethod(
                subscription.id,
                setupIntent.payment_method,
              );
              onPaymentMethodReady();
            } catch {
              setError("Failed to save payment method. Please try again.");
            } finally {
              setSaving(false);
            }
          }}
          onError={(stripeError?: StripeError) => {
            setError(
              stripeError?.message ||
                "Failed to add payment method. Please try again.",
            );
          }}
          returnUrl={`${location.pathname}${location.search}`}
          subscription={subscription}
        />

        <div className={styles.developerPayActions}>
          <Button
            className="slapShotButton"
            disabled={saving}
            onClick={() => addPaymentMethodRef.current?.confirm()}
          >
            {saving ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : (
              "Add card & Upgrade"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePaymentPanel;
